import Plyr from 'plyr';
import { createMachine, assign, spawn } from 'xstate';

import { prefersReducedMotion } from '@sg-core/resources/assets/js/utils/media';

import { loadingSourcesMachine, isHlsSrc, loadingSourcesMachineData } from './loading-sources';

import type Hls from 'hls.js';

const VIDEO_PLAYER_SELECTOR = '.phxsg-video-player, .video-player';

interface VideoStateContext {
  autoplay: boolean;
  enablePlyr: boolean;
  hls: Hls | null;
  lazyOptions: IntersectionObserverInit | null;
  mutationObserver: MutationObserver | null;
  intersectionObserver: IntersectionObserver | null;
  plyr: Plyr | null;
  plyrOptions: Plyr.Options | null;
  posters: Map<MediaQueryList, { src: string; ref: unknown }> | null;
  posterSrc: string | null;
  prefetch: boolean;
  sourceElement:
    | ((HTMLSourceElement | HTMLVideoElement) & {
        dataset: DOMStringMap & { currentSrc?: string };
        src?: string;
      })
    | null;
  sources: Map<MediaQueryList, { src: string; ref: unknown }> | null;
  videoElement: HTMLVideoElement | HTMLIFrameElement | null;
  videoSrc: string | null;
  playController: AbortController | false;
  currentTime?: number | null;
  abortController?: AbortController | null;
}

interface LoadingDoneEvent {
  type: string;
  data: {
    abortController: AbortController | null;
    hls: Hls | null;
    plyr: Plyr | null;
    sourceElement:
      | ((HTMLSourceElement | HTMLVideoElement) & {
          dataset: DOMStringMap & { currentSrc?: string };
          src?: string;
        })
      | null;
    videoElement: HTMLVideoElement | HTMLIFrameElement | null;
    videoSrc: string | null;
  };
}

interface ParseDoneEvent {
  type: string;
  data: {
    assets: { src: string; media?: string }[];
    sourceElement?: HTMLSourceElement | null;
  };
}

interface MediaQueryChangeEvent {
  type: string;
  mediaQuery: MediaQueryList;
}

interface SeekEvent {
  type: string;
  value: number;
}

interface ErrorEvent {
  type: string;
  data: unknown;
}

type VideoStateEvent =
  | { type: 'SHOW' }
  | { type: 'HIDE' }
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'TOGGLE' }
  | { type: 'SOURCES_CHANGE' }
  | { type: 'SOURCES_MEDIA_QUERY_CHANGE' }
  | { type: 'POSTER_MEDIA_QUERY_CHANGE' }
  | { type: 'ERROR' }
  | { type: 'RETRY' }
  | LoadingDoneEvent
  | ParseDoneEvent
  | MediaQueryChangeEvent
  | SeekEvent
  | ErrorEvent;

export const videoStateMachine = createMachine<VideoStateContext, VideoStateEvent>({
  id: 'video',
  predictableActionArguments: true,
  preserveActionOrder: true,

  context: {
    autoplay: false,
    enablePlyr: false,
    hls: null,
    lazyOptions: null,
    mutationObserver: null,
    intersectionObserver: null,
    plyr: null,
    plyrOptions: null,
    posters: null,
    posterSrc: null,
    prefetch: false,
    sourceElement: null,
    sources: null,
    videoElement: null,
    videoSrc: null,
    playController: false,
  },

  initial: 'initialize',

  states: {
    /**
     * Parse responsive video and posters URLs. This state is normally
     * only transient and only traversed once at the beginning of the
     * machine lifecycle.
     */
    initialize: {
      id: 'initialize',
      initial: 'parseSources',

      states: {
        /**
         * Parse `<source>` elements and attach media queries on each
         * resulting URL if available. Continue to the next state when
         * the promise has resolved.
         */
        parseSources: {
          invoke: {
            id: 'parseSources',
            src: 'parseSources',
            onDone: [
              {
                target: 'parsePosters',
                actions: 'attachSourcesMediaQuery',
                cond: 'hasReturnedAssets',
              },
              { target: 'parsePosters' },
            ],
          },
        },

        /**
         * Parse `[data-]poster` attributes and attach media queries on each
         * resulting URL if available. Continue to the next state when
         * the promise has resolved.
         */
        parsePosters: {
          invoke: {
            id: 'parsePosters',
            src: 'parsePosters',
            onDone: [
              {
                target: 'instantiatePlyr',
                actions: ['attachPostersMediaQuery', 'updatePosterAttribute'],
                cond: 'hasReturnedAssets',
              },
              { target: 'instantiatePlyr' },
            ],
          },
        },

        /**
         * Create a `Plyr` instance if option is set and no instance
         * exists in the context yet
         */
        instantiatePlyr: {
          always: [
            {
              target: 'resetVideoElement',
              actions: 'instantiatePlyr',
              cond: 'requirePlyr',
            },
            { target: 'createObservers' },
          ],
        },

        /**
         * Plyr has the unfortunate habit of replacing DOM elements. It breaks
         * many things, including the intersection observer. We need to wait
         * for the element to be replaced before we can get a new reference to
         * the element and instantiate the observer.
         */
        resetVideoElement: {
          after: {
            150: {
              target: 'createObservers',
              actions: 'resetVideoElementAfterPlyrInit',
            },
          },
        },

        createObservers: {
          /**
           * We've chosen to always use an intersection observer to handle the
           * video player play state since it's way better for global performances
           * and it doesn't interfere with the user experience
           */
          entry: 'handleVisibility',

          always: [
            /**
             * We listen for dynamic sources modifications to retrigger the
             * parsing process
             */
            {
              target: '#idle',
              actions: 'handleSourcesChange',
              cond: 'hasNoMutationObserver',
            },
            { target: '#idle' },
          ],
        },
      },
    },

    /**
     * The first state after the initialize one. It's used to wait for the
     * intersection observer `SHOW` event to actually kick in the sources
     * loading process to fully conforms to the lazy loading expectations
     */
    idle: {
      id: 'idle',
      initial: 'wait',

      states: {
        /**
         * Wait for the `SHOW` event to lazy load the sources
         */
        wait: {
          on: {
            SHOW: { target: 'load' },
          },
        },

        /**
         * Load the sources via the `loadingSources` machine and choose wether
         * to play or pause the media based on the `autoplay` option
         */
        load: {
          tags: ['loading'],
          exit: 'loadMedia',
          invoke: {
            id: 'loadSources',
            src: loadingSourcesMachine,
            data: loadingSourcesMachineData,
            onDone: [
              {
                target: '#visible.play',
                actions: 'loadingReturnData',
                cond: 'canAutoplay',
              },
              {
                target: '#visible.pause',
                actions: 'loadingReturnData',
              },
            ],
          },
        },
      },
    },

    /**
     * State used when the intersection observer has explicitly called
     * the `HIDE` event.
     */
    hidden: {
      id: 'hidden',
      initial: 'pause',
      entry: ['pause', 'abortPlay'],

      states: {
        /**
         * Represent a `pause` action triggered by the user
         */
        pause: {
          initial: 'wait',

          states: {
            wait: {
              on: {
                /**
                 * If the intersection observer triggers the `SHOW` event,
                 * that means the media is now visible but we want to honor
                 * previous user action by returning to the corresponding
                 * pause state
                 */
                SHOW: { target: '#visible.pause' },

                /**
                 * Event triggered by the MediaQueryList objects attach to
                 * every source string. If it happens, we want to load the
                 * new source
                 */
                SOURCES_MEDIA_QUERY_CHANGE: {
                  target: 'load',
                },
              },
            },

            /**
             * Load the sources via the `loadingSources` machine and get
             * back to the `pending` state to wait for the `SHOW` event
             */
            load: {
              tags: ['loading'],
              exit: 'loadMedia',
              invoke: {
                src: loadingSourcesMachine,
                data: loadingSourcesMachineData,
                onDone: {
                  target: 'wait',
                  actions: 'loadingReturnData',
                },
              },
            },
          },
        },

        /**
         * Represent a `pause` action triggered by the intersection observer
         * when the media is no more in the viewport.
         */
        suspend: {
          initial: 'wait',

          states: {
            wait: {
              on: {
                /**
                 * If the intersection observer triggers the `SHOW` event,
                 * that means the media is now visible. We can safely resume
                 * the playback
                 */
                SHOW: { target: '#visible.play' },

                /**
                 * Event triggered by the MediaQueryList objects attach to
                 * every source string. If it happens, we want to load the
                 * new source
                 */
                SOURCES_MEDIA_QUERY_CHANGE: {
                  target: 'load',
                },
              },
            },

            /**
             * Load the sources via the `loadingSources` machine and get
             * back to the `pending` state to wait for the `SHOW` event
             */
            load: {
              tags: ['loading'],
              exit: 'loadMedia',
              invoke: {
                src: loadingSourcesMachine,
                data: loadingSourcesMachineData,
                onDone: {
                  target: 'wait',
                  actions: 'loadingReturnData',
                },
              },
            },
          },
        },

        /**
         * Represent a `stop` action triggered by the user
         */
        stop: {
          initial: 'wait',

          states: {
            wait: {
              on: {
                /**
                 * If the intersection observer triggers the `SHOW` event,
                 * that means the media is now visible but we want to honor
                 * previous user action by returning to the corresponding
                 * pause state
                 */
                SHOW: { target: '#visible.stop' },

                /**
                 * Event triggered by the MediaQueryList objects attach to
                 * every source string. If it happens, we want to load the
                 * new source
                 */
                SOURCES_MEDIA_QUERY_CHANGE: {
                  target: 'load',
                },
              },
            },

            /**
             * Load the sources via the `loadingSources` machine and get
             * back to the `pending` state to wait for the `SHOW` event
             */
            load: {
              tags: ['loading'],
              exit: 'loadMedia',
              invoke: {
                src: loadingSourcesMachine,
                data: loadingSourcesMachineData,
                onDone: {
                  target: 'wait',
                  actions: 'loadingReturnData',
                },
              },
            },
          },
        },
      },
    },

    /**
     * State used when the intersection observer has explicitly called
     * the `SHOW` event.
     */
    visible: {
      id: 'visible',

      states: {
        /**
         * Pause the media playback
         */
        pause: {
          entry: ['pause', 'abortPlay'],

          on: {
            /**
             * When `play` event is triggered manually
             */
            PLAY: { target: 'play' },

            /**
             * Plyr uses the `play` event as a toggle mechanism so we need to
             * play and pause the playback accordingly
             */
            TOGGLE: { target: 'play' },

            /**
             * Seek the playback position to the provided value
             */
            SEEK: { actions: 'seekTo' },

            /**
             * The media is no more visible to the user
             */
            HIDE: { target: '#hidden.pause' },
          },
        },

        lowPower: {
          entry: 'listenPlayEvent',

          on: {
            /**
             * When `play` event is triggered manually
             */
            PLAY: { target: 'play' },

            /**
             * Plyr uses the `play` event as a toggle mechanism so we need to
             * play and pause the playback accordingly
             */
            TOGGLE: { target: 'play' },
          },

          exit: 'dropPlayEvent',
        },

        /**
         * Start or resume the media playback
         */
        play: {
          entry: ['disableAutoplay', 'createPlayController'],

          /**
           * HTMLMediaElement.play() actually returns a Promise
           */
          invoke: {
            src: 'play',
            onDone: {
              actions: 'resetCurrentTime',
            },
            onError: {
              target: 'lowPower',
              cond: 'isLowPowerMode',
            },
          },

          on: {
            /**
             * Pause the media and disable further auto play triggered
             * by the IntersectionObserver
             */
            PAUSE: { target: 'pause' },

            /**
             * Toggle the playback and disable further auto play triggered
             * by the IntersectionObserver
             */
            TOGGLE: { target: 'stop' },

            /**
             * Seek the playback position to the provided value
             */
            SEEK: { actions: 'seekTo' },

            /**
             * Pause the playback while the media is no more visible to
             * the user. Use by the intersection observer, but can also
             * be triggered manually to suspend / resume the playback
             * while honoring previous user actions
             */
            HIDE: { target: '#hidden.suspend' },

            /**
             * When sources are dynamically updated while the media is playing,
             * we reinitialize the machine to take the new sources and specify
             * we want to play the video back after the process
             */
            SOURCES_CHANGE: {
              target: '#initialize',
              actions: 'enableAutoplay',
            },
          },
        },

        /**
         * Stop the media. The semantic is different from the pause
         * state to allow for more granular behavior. To play the media
         * again, only the `TOGGLE` event will work.
         */
        stop: {
          entry: ['pause', 'abortPlay'],

          on: {
            /**
             * Special event to force the media to play, invoking the PLAY
             * event on a `stopped` will not have any effect
             */
            TOGGLE: { target: 'play' },
            HIDE: { target: '#hidden.stop' },
          },
        },

        /**
         * Load the sources via the `loadingSources` machine and get
         * back to the `play` or `pause` state based on the previous
         * playback state of the video
         */
        load: {
          tags: ['loading'],
          exit: 'loadMedia',
          invoke: {
            src: loadingSourcesMachine,
            data: loadingSourcesMachineData,
            onDone: [
              {
                target: 'play',
                actions: 'loadingReturnData',
                cond: 'isPlaying',
              },
              {
                target: 'pause',
                actions: 'loadingReturnData',
              },
            ],
          },
        },
      },

      on: {
        /**
         * Event triggered by the MediaQueryList objects attach to
         * every source string
         */
        SOURCES_MEDIA_QUERY_CHANGE: {
          target: '.load',
          actions: 'sourcesMediaQueryChange',
        },
      },
    },

    /**
     * Handle errors raised during the machine lifecycle.
     */
    error: {
      id: 'error',
      entry: 'logError',
      on: {
        /**
         * Try to restart the complete sequence of the machine
         */
        RETRY: { target: 'initialize' },
      },
    },
  },

  /**
   * Global events that must be handle on every state nodes
   */
  on: {
    /**
     * Event triggered by the MediaQueryList objects attach to
     * every poster string. We don't need to take any further
     * action since the playback state has no influence on that
     */
    POSTER_MEDIA_QUERY_CHANGE: {
      actions: 'posterMediaQueryChange',
    },

    /**
     * When sources are dynamically updated, we reinitialize
     * the machine to take the new sources and wait for the
     * initial `SHOW` event
     */
    SOURCES_CHANGE: {
      target: 'initialize',
    },

    ERROR: {
      target: 'error',
    },
  },
});

export const videoStateMachineConfig = {
  guards: {
    /**
     * Check if the event returns with an `assets` property defined
     */
    hasReturnedAssets: (_: VideoStateContext, event: VideoStateEvent) => {
      return 'data' in event && event.data && typeof event.data === 'object' && 'assets' in event.data;
    },

    /**
     * Check that no MutationObserver has been created yet
     */
    hasNoMutationObserver: ({ mutationObserver }: VideoStateContext) => mutationObserver === null,

    /**
     * Check that no IntersectionObserver has been created yet
     */
    hasNoIntersectionObserver: ({ intersectionObserver }: VideoStateContext) => intersectionObserver === null,

    /**
     * Check if we have the right to `autoplay` the media.
     */
    canAutoplay: ({ videoElement, autoplay }: VideoStateContext) => {
      return videoElement && (videoElement.hasAttribute('autoplay') || autoplay) && !prefersReducedMotion();
    },

    /**
     * Check if the media is currently in a playing state
     */
    isPlaying: ({ videoElement }: VideoStateContext) => {
      return videoElement instanceof HTMLVideoElement && !videoElement.paused;
    },

    /**
     * Check if we need to instantiate a `Plyr` instance
     */
    requirePlyr: ({ enablePlyr, plyr }: VideoStateContext) => enablePlyr && plyr === null,

    /**
     * Check if the rejected event means "low power mode" in Safari browsers
     */
    isLowPowerMode: (_: VideoStateContext, event: VideoStateEvent) => {
      return (
        'data' in event &&
        event.data &&
        typeof event.data === 'object' &&
        'name' in event.data &&
        event.data.name === 'NotAllowedError'
      );
    },
  },

  actions: {
    /**
     * When the Plyr instance is created, it replaces the video element with
     * a new one. In that case, we need to get a new reference to the element.
     */
    resetVideoElementAfterPlyrInit: assign({
      videoElement: ({ videoElement, plyr }: VideoStateContext) => {
        /**
         * If the video element is still connected (in the DOM), it means that the
         * reference we have is still valid despite the Plyr instanciation.
         */
        if (!videoElement || (videoElement instanceof HTMLElement && videoElement.isConnected) || !plyr) {
          return videoElement;
        }

        const mediaElement = (plyr as unknown as { media: HTMLMediaElement }).media;
        const newVideoElement =
          (document.getElementById(mediaElement.id) as HTMLVideoElement | HTMLIFrameElement) || videoElement;

        return newVideoElement;
      },
    }),

    /**
     * Instantiate the `Plyr` player and override it's play / pause listeners
     * to be compliant with our implementation
     */
    instantiatePlyr: assign({
      plyr: ({ videoElement, plyrOptions }: VideoStateContext) => {
        if (!videoElement) {
          return null;
        }

        /**
         * The callback used to send events to the upper machine
         */
        const callbackRef = spawn((callback, receive) => receive(callback));

        const plyrElement = (
          videoElement instanceof HTMLIFrameElement ? videoElement.parentElement : videoElement
        ) as HTMLElement;

        if (!plyrElement) {
          return null;
        }

        const plyrInstance = new Plyr(plyrElement, {
          ...plyrOptions,
          listeners: {
            play: () => {
              /**
               * Plyr use a `toggle` mechanism to play / pause the media so
               * we need to create a special event
               */
              callbackRef.send('TOGGLE');
              return false;
            },
            pause: () => {
              callbackRef.send('PAUSE');
              return false;
            },
          },
        });

        /**
         * When the video ends, Plyr detects the native `pause` event
         * but the `listeners.pause` callback (which only handles button
         * clicks) is not triggered. This leaves the machine stuck in
         * `visible.play`. Listening for the `ended` event ensures the
         * machine transitions to `visible.pause` so a single click can
         * replay the video.
         */
        plyrInstance.on('ended', () => {
          callbackRef.send('PAUSE');
        });

        return plyrInstance;
      },
    }),

    /**
     * When the child machine reach its final state, we reassign its context
     * values in the current machine for later use
     */
    loadingReturnData: assign({
      abortController: (_: VideoStateContext, event: VideoStateEvent) => {
        return 'data' in event && event.data && typeof event.data === 'object' && 'abortController' in event.data
          ? (event.data as LoadingDoneEvent['data']).abortController
          : null;
      },
      hls: (_: VideoStateContext, event: VideoStateEvent) => {
        return 'data' in event && event.data && typeof event.data === 'object' && 'hls' in event.data
          ? (event.data as LoadingDoneEvent['data']).hls
          : null;
      },
      plyr: (_: VideoStateContext, event: VideoStateEvent) => {
        return 'data' in event && event.data && typeof event.data === 'object' && 'plyr' in event.data
          ? (event.data as LoadingDoneEvent['data']).plyr
          : null;
      },
      sourceElement: (_: VideoStateContext, event: VideoStateEvent) => {
        return 'data' in event && event.data && typeof event.data === 'object' && 'sourceElement' in event.data
          ? (event.data as LoadingDoneEvent['data']).sourceElement
          : null;
      },
      videoElement: (_: VideoStateContext, event: VideoStateEvent) => {
        return 'data' in event && event.data && typeof event.data === 'object' && 'videoElement' in event.data
          ? (event.data as LoadingDoneEvent['data']).videoElement
          : null;
      },
      videoSrc: (_: VideoStateContext, event: VideoStateEvent) => {
        return 'data' in event && event.data && typeof event.data === 'object' && 'videoSrc' in event.data
          ? (event.data as LoadingDoneEvent['data']).videoSrc
          : null;
      },
    }),

    /**
     * Set the video poster attribute with the current value
     */
    updatePosterAttribute({ videoElement, posterSrc }: VideoStateContext) {
      if (videoElement && posterSrc) {
        videoElement.setAttribute('poster', posterSrc);
      }
    },

    /**
     * On a source media query change event, we update the context accordingly
     * and keep track of the media current position to be able to seamlessly
     * continue the playback afterward
     */
    sourcesMediaQueryChange: assign({
      currentTime: ({ videoElement }: VideoStateContext) => {
        return videoElement instanceof HTMLVideoElement ? videoElement.currentTime : null;
      },
      videoSrc: ({ sources }: VideoStateContext, event: VideoStateEvent) => {
        if ('mediaQuery' in event && event.mediaQuery && sources) {
          return onMediaQueryChange(event.mediaQuery as MediaQueryList, sources);
        }
        return null;
      },
    }),

    /**
     * On a poster media query change event, we update the context accordingly
     * and update the DOM element right away since we don't need to rely on
     * specific loading logic for that attribute
     */
    posterMediaQueryChange: assign({
      posterSrc: ({ videoElement, posters }: VideoStateContext, event: VideoStateEvent) => {
        if (!('mediaQuery' in event) || !event.mediaQuery || !posters || !videoElement) {
          return null;
        }

        const src = onMediaQueryChange(event.mediaQuery as MediaQueryList, posters);

        if (src) {
          videoElement.setAttribute('poster', src);
        }

        return src;
      },
    }),

    /**
     * Create and attach MediaQueryList object to every source's URL
     * previously parsed and validated. Update the context accordingly
     */
    attachSourcesMediaQuery: assign(({ sources }: VideoStateContext, event: VideoStateEvent) => {
      /**
       * Clear previously set media query listeners to avoid
       * multiple handlers on sources modifications
       */
      if (sources instanceof Map) {
        for (const mq of sources.keys()) {
          mq.onchange = null;
        }

        sources.clear();
      }

      if (!('data' in event) || !event.data || typeof event.data !== 'object' || !('assets' in event.data)) {
        return {};
      }

      const parseEvent = event.data as ParseDoneEvent['data'];
      const [newSources, videoSrc] = attachMediaQuery(parseEvent.assets, 'SOURCES_MEDIA_QUERY_CHANGE');

      return {
        sources: new Map(newSources),
        videoSrc,
        sourceElement: parseEvent.sourceElement || null,
      };
    }),

    /**
     * Create and attach MediaQueryList object to every poster's URL
     * previously parsed and validated. Update the context accordingly
     */
    attachPostersMediaQuery: assign(({ posters }: VideoStateContext, event: VideoStateEvent) => {
      /**
       * Clear previously set media query listeners to avoid
       * multiple handlers on posters modifications
       */
      if (posters instanceof Map) {
        for (const mq of posters.keys()) {
          mq.onchange = null;
        }

        posters.clear();
      }

      if (!('data' in event) || !event.data || typeof event.data !== 'object' || !('assets' in event.data)) {
        return {};
      }

      const parseEvent = event.data as ParseDoneEvent['data'];
      const [newPosters, posterSrc] = attachMediaQuery(parseEvent.assets, 'POSTER_MEDIA_QUERY_CHANGE');

      return {
        posters: new Map(newPosters),
        posterSrc,
      };
    }),

    /**
     * Registers a mutation observer to handle cases where video sources are
     * dynamically updated or changed
     */
    handleSourcesChange: assign({
      mutationObserver: ({ videoElement }: VideoStateContext) => {
        if (!videoElement) {
          return null;
        }

        const callbackRef = spawn((callback, receive) => receive(callback));

        const observer = new MutationObserver(() => {
          callbackRef.send({ type: 'SOURCES_CHANGE' });
        });

        observer.observe(videoElement, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['data-src'],
        });

        return observer;
      },
    }),

    /**
     * Registers an intersection observer that takes care of loading, playing or
     * pausing the video based on its visibility
     */
    handleVisibility: assign({
      intersectionObserver: ({ videoElement, lazyOptions, intersectionObserver }: VideoStateContext) => {
        if (!videoElement) {
          return null;
        }

        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }

        /**
         * If `videoElement` is an iFrame, such as with Youtube embedded videos, the
         * intersection observer seems to be unable to correctly track its visible
         * state. We then fallback to observe the upper container element in the
         * DOM tree by selecting the closest ancestor
         */
        const observedElement =
          videoElement instanceof HTMLIFrameElement
            ? (videoElement.closest(VIDEO_PLAYER_SELECTOR) as HTMLElement)
            : videoElement;

        if (!observedElement) {
          return null;
        }

        const callbackRef = spawn((callback, receive) => receive(callback));

        const visibleEvent = new CustomEvent('player-visible', {
          bubbles: true,
          detail: { videoPlayer: observedElement },
        });

        const hiddenEvent = new CustomEvent('player-hidden', {
          bubbles: true,
          detail: { videoPlayer: observedElement },
        });

        const observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              observedElement.dispatchEvent(visibleEvent);
              callbackRef.send({ type: 'SHOW' });
            } else {
              observedElement.dispatchEvent(hiddenEvent);
              callbackRef.send({ type: 'HIDE' });
            }
          }
        }, lazyOptions || undefined);

        observer.observe(observedElement);

        return observer;
      },
    }),

    /**
     * Reset current media position in case we don't need it anymore
     */
    resetCurrentTime: assign({ currentTime: null }),

    /**
     * In a "low power" mode, we want to catch `play` event triggered
     * automatically by the user platform since the UX/UI can be specific
     * to the OS / browser
     */
    listenPlayEvent: assign(({ videoElement }: VideoStateContext) => {
      if (!videoElement) {
        return {};
      }

      const callbackRef = spawn((callback, receive) => receive(callback));

      videoElement.onplay = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();

        callbackRef.send('PLAY');
      };

      return {};
    }),

    /**
     * Drop the `onplay` handler when no more needed
     */
    dropPlayEvent: ({ videoElement }: VideoStateContext) => {
      if (videoElement) {
        videoElement.onplay = null;
      }
    },

    /**
     * Actually load any source defined for the media element.
     *
     * When hls.js manages the source via its own MediaSource, the video
     * element's `src` is a `blob:` URL created by `attachMedia()`. At
     * that point the MediaSource `sourceopen` event has not yet fired
     * (it is async). Calling `videoElement.load()` here would reset
     * the element, abort the pending MediaSource setup, and silently
     * prevent the m3u8 playlist from ever being fetched.
     *
     * Native HLS (Safari) sets a regular URL on `video.src`, so `load()`
     * is still needed there. Non-HLS sources set `src` on the `<source>`
     * child and clear `video.src`, so `load()` is also needed.
     */
    loadMedia({ videoElement }: VideoStateContext) {
      if (!(videoElement instanceof HTMLVideoElement)) {
        return;
      }

      if (videoElement.src.startsWith('blob:')) {
        return;
      }

      videoElement.load();
    },

    /**
     * Pause media element or the `Plyr` instance
     */
    pause({ videoElement, plyr }: VideoStateContext) {
      if (plyr) {
        plyr.pause();
      } else if (videoElement instanceof HTMLVideoElement) {
        videoElement.pause();
      }
    },

    /**
     * Seek the playback position to the provided value
     */
    seekTo({ videoElement }: VideoStateContext, event: VideoStateEvent) {
      if (!('value' in event) || typeof event.value !== 'number' || !(videoElement instanceof HTMLVideoElement)) {
        return;
      }

      videoElement.currentTime = event.value;
    },

    /**
     * Log error for debugging purpose
     */
    logError: (_: VideoStateContext, _event: VideoStateEvent) => {
      // Error logging removed for production
    },

    /**
     * Use to force the playback of the media after an event
     * that require multiple steps in the state machine
     */
    enableAutoplay: assign({ autoplay: true }),
    disableAutoplay: assign({ autoplay: false }),

    /**
     * Create a new `AbortController` instance to be able to abort
     * the media playback when needed. Since the `play` event is
     * asynchronous, we need to be able to cancel it if the user
     * interact with the media in the meantime.
     */
    createPlayController: assign({
      playController: () => new AbortController(),
    }),
    abortPlay: ({ playController }: VideoStateContext) => {
      if (playController instanceof AbortController) {
        playController.abort();
      }
    },
  },

  services: {
    /**
     * Parse `data-src` on every `<source>` node. Each element must
     * contains a `type` attribute (mandatory in the HTML spec) to
     * correctly determine the playable sources
     */
    parseSources({ videoElement }: VideoStateContext) {
      return async () => {
        if (!videoElement) {
          return {};
        }

        for (const sourceElement of videoElement.querySelectorAll('source')) {
          const videoSrc = sourceElement.dataset.src || sourceElement.getAttribute('src') || '';

          if (
            videoSrc &&
            videoElement instanceof HTMLVideoElement &&
            (videoElement.canPlayType(sourceElement.type) || isHlsSrc({ videoSrc }))
          ) {
            return {
              sourceElement,
              assets: videoSrc.split(',').map(parseSrcString),
            };
          }
        }

        return {};
      };
    },

    /**
     * Parse `data-poster` or `poster` attributes on `<video>` element
     */
    parsePosters({ videoElement }: VideoStateContext) {
      return async () => {
        if (!videoElement) {
          return { assets: [] };
        }

        const posters = videoElement.dataset.poster || videoElement.getAttribute('poster') || '';

        return {
          assets: posters.split(',').map(parseSrcString),
        };
      };
    },

    /**
     * Play the media and seek directly to the last known position,
     * taking into account that Plyr instance behave differently
     * than a regular `HTMLMediaElement`.
     */
    play({ plyr, videoElement, currentTime, playController }: VideoStateContext, event: VideoStateEvent) {
      if (currentTime && videoElement instanceof HTMLVideoElement) {
        videoElement.currentTime = currentTime;
      }

      /**
       * Delay the invocation when the event is not a direct user
       * request to play the media (e.g. a dynamic source update) to
       * prevent a glitch when poster and sources are swapped
       */
      return new Promise<void>((resolve, reject) => {
        if (!(playController instanceof AbortController) || playController.signal.aborted) {
          return;
        }

        setTimeout(
          () => {
            if (!(playController instanceof AbortController) || playController.signal.aborted) {
              return;
            }

            if (videoElement instanceof HTMLVideoElement) {
              if (videoElement.ended) {
                videoElement.currentTime = 0;
              }
              videoElement
                .play()
                .then(() => resolve())
                .catch(() => reject(new Error('An error occurred while trying to play the media')));
            } else if (plyr) {
              plyr.play();
              resolve();
            } else {
              reject(new Error('No valid video element or plyr instance'));
            }
          },
          event.type === 'PLAY' || event.type === 'TOGGLE' ? 0 : 750,
        );
      });
    },
  },
};

/**
 * Parse a string in the form of 'video-sm.mp4 (max-width: 767px)' and return
 * an object of the form `{ src: 'video-sm.mp4', media: '(max-width: 767px)' }`
 */
function parseSrcString(str: string): { src: string; media?: string } {
  const match = decodeURI(str)
    .trim()
    .match(/(?<src>[^\s]*)\s?(?<media>\(.*\))?/);

  const groups = match?.groups as { src?: string; media?: string } | undefined;
  return groups?.src ? { src: groups.src, media: groups.media } : { src: str };
}

/**
 * Attach a media query object for each url in `assets` and produce
 * a `Map` whose keys are the resulting media query object and values are
 * the actual resource URI. It also returns the `currentAsset` string based
 * on media queries results.
 *
 * The `onchange` handler use the spawned callback functionalities
 * (https://xstate.js.org/docs/guides/actors.html#spawning-callbacks) to
 * communicate the screen size changes to the parent machine.
 */
function attachMediaQuery(
  assets: { src: string; media?: string }[],
  eventType: string,
): [[MediaQueryList, { src: string; ref: unknown }][], string | null] {
  const assetsMap: [MediaQueryList, { src: string; ref: unknown }][] = [];
  let currentAsset: string | null = null;

  for (const asset of assets) {
    if (!asset.src) {
      continue;
    }

    const mq = window.matchMedia(asset.media || 'screen');

    /**
     * Spawn a callback to handle the change event of the MediaQueryList
     */
    const ref = spawn((callback, receive) => {
      receive((e: unknown) => {
        const event = e as { target: MediaQueryList };
        callback({
          type: eventType,
          mediaQuery: event.target,
        });
      });
    });

    /**
     * `ref.send` will passthrough the media query `change` event to the
     * spawned `ref` define just above. The `receive` function will then catch
     * the event and use the `callback` to actually send the right event to
     * the parent machine.
     *
     * We use the event handler instead of attaching an event listener
     * here for implementation simplicity since we don't need to
     * remove handler later on.
     */
    mq.onchange = ref.send as ((this: MediaQueryList, ev: MediaQueryListEvent) => void) | null;

    if (!currentAsset && mq.matches) {
      currentAsset = asset.src;
    }

    assetsMap.push([mq, { src: asset.src, ref }]);
  }

  return [assetsMap, currentAsset];
}

/**
 * Return the new value depending on if the `mediaQuery` matches or not
 */
function onMediaQueryChange(
  mediaQuery: MediaQueryList,
  sources: Map<MediaQueryList, { src: string; ref: unknown }>,
): string | null {
  const currentMediaQuery = getSmallestMediaQueryMatch(mediaQuery, sources);

  return sources.get(currentMediaQuery)?.src || null;
}

/**
 * Get the first media query object who matches.
 *
 * The logic is based on the assumption that we only support `max-width` media queries.
 *
 * When the event `matches`, the viewport is shrinking and we can safely use
 * the corresponding URI. If the event doesn't `matches`, the viewport is
 * expending and we need to get the next URI available.
 */
function getSmallestMediaQueryMatch(
  mediaQuery: MediaQueryList,
  sourcesMap: Map<MediaQueryList, { src: string; ref: unknown }>,
): MediaQueryList {
  let mq = mediaQuery;

  if (!mq.matches) {
    const keys = Array.from(sourcesMap.keys());
    let index = keys.indexOf(mq);

    /**
     * Get the first valid mediaQuery or the last one if nothing else matches
     */
    do {
      mq = keys[++index];
    } while (mq && !mq.matches && index < keys.length);
  }

  return mq;
}
